import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Border,
  Button,
  Colors,
  Divider,
  IconButton,
  Icons,
  Skeleton,
  Typography,
  classNames,
  createClasses,
  pxToRem,
  spacing,
} from '@kp/react-ui';

import { KpiCardEmpty } from './KpiCardEmpty';

export const KPI_CARD_MIN_HEIGHT = pxToRem(224);

const classes = createClasses({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('md'),
    padding: spacing('md'),
    borderRadius: Border.Radius.Medium,
    border: `1px solid ${Colors.Neutral.border}`,
    backgroundColor: Colors.Neutral.backgroundMedium,
    minHeight: KPI_CARD_MIN_HEIGHT,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing('xs'),
  },
  header: {
    flex: 1,
    minWidth: 0,
  },
  link: {
    color: Colors.Neutral.icon,
    flexShrink: 0,
  },
  body: {
    height: pxToRem(100),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: spacing('xs'),
  },
  skeletonValue: {
    width: pxToRem(96),
    height: pxToRem(32),
    borderRadius: Border.Radius.Small,
  },
  skeletonLine: {
    width: '70%',
    height: pxToRem(16),
    borderRadius: Border.Radius.Small,
  },
  weak: {
    color: Colors.Neutral.textWeak,
  },
  retry: {
    alignSelf: 'flex-start',
  },
  footer: {
    position: 'absolute',
    right: pxToRem(15),
    bottom: pxToRem(15),
  },
});

export interface KpiCardProps {
  header: React.ReactNode;
  /** Deep link into Insights; without one the open-in link is left out. */
  href?: string;
  linkLabel: string;
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  emptyState?: React.ReactNode;
  onRetry?: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const KpiCard: React.FC<KpiCardProps> = ({
  header,
  href,
  linkLabel,
  isLoading = false,
  isError = false,
  isEmpty = false,
  emptyState,
  onRetry,
  footer,
  children,
  className,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();

  const renderBody = () => {
    if (isError) {
      return (
        <div className={classes.body} role="alert">
          <Typography variant="bodySmall" className={classes.weak}>
            {t('common.loadError')}
          </Typography>
          <Button
            variant="secondary"
            size="small"
            className={classes.retry}
            onClick={onRetry}
          >
            {t('common.retry')}
          </Button>
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className={classes.body} aria-busy="true">
          <Skeleton className={classes.skeletonValue} />
          <Skeleton className={classes.skeletonLine} />
        </div>
      );
    }
    if (isEmpty) {
      return <KpiCardEmpty />;
    }
    return <div className={classes.body}>{children}</div>;
  };

  const showEmptyState = !isError && !isLoading && isEmpty && !!emptyState;

  return (
    <div className={classNames(classes.root, className)} data-testid={testId}>
      <div className={classes.headerRow}>
        <div className={classes.header}>{header}</div>
        {href && (
          <IconButton
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={linkLabel}
            variant="tertiary"
            size="small"
            icon={Icons.IconOpenIn}
            className={classes.link}
          />
        )}
      </div>
      {showEmptyState ? (
        emptyState
      ) : (
        <>
          <Divider />
          {renderBody()}
        </>
      )}
      {footer && <div className={classes.footer}>{footer}</div>}
    </div>
  );
};
